import { type DetailedHTMLProps, type HTMLAttributes, type ReactNode, useLayoutEffect, useRef } from 'react';
import { GrowContent } from './Grow.styles';

interface TGrowProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  /**
   * isGrown state, must be false by default
   */
  isGrown: boolean;
  /**
   * contentHeight, must change on children's height
   */
  contentHeight?: number;
  children: ReactNode;
  className?: string;
}

const grow = (element: HTMLDivElement, shrinkedHeight?: number, fullHeight?: number) => {
  if (!shrinkedHeight || !fullHeight) return;

  requestAnimationFrame(() => {
    element.style.height = `${shrinkedHeight}px`;
    element.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      element.style.height = `${fullHeight}px`;
    });
  });
};

const shrink = (element: HTMLDivElement, shrinkedHeight?: number, fullHeight?: number) => {
  if (!shrinkedHeight || !fullHeight) return;

  requestAnimationFrame(() => {
    element.style.height = `${fullHeight}px`;
    element.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      element.style.height = `${shrinkedHeight}px`;
    });
  });
};

/* 
  HOW IT WORKS:
  Grow receives isGrown and contentHeight props, with contentHeight being the reactive height of its children.
  IMPORTANT: isGrown must initially be false; true initial state is not yet supported.
  Grow renders its children and remembers their height when isGrown is false (shrinkedHeight).
  When isGrown is true, Grow expands to the full height (fullHeight), remembering this height.
  Toggling isGrown transitions between shrinkedHeight and fullHeight.
  Grow does not remove part of children from the DOM when shrunk; it only reduces its height.
  KNOWN ISSUE: Grow misses bottom padding when toggling isGrown true -> false
*/
const Grow = ({ isGrown, contentHeight, children, className, ...others }: TGrowProps) => {
  const growRef = useRef<HTMLDivElement>(null);
  const fullHeight = useRef<number | undefined>();
  const shrinkedHeight = useRef<number | undefined>();

  useLayoutEffect(() => {
    const element = growRef.current;
    if (!element || !contentHeight) return;

    if (!isGrown) {
      // this fix issue when Grow wraps a short component -> switch to a taller component
      // content height when grow will change from full height -> shrinked height. We pick shrinked height.
      if (shrinkedHeight.current !== contentHeight && contentHeight !== fullHeight.current) {
        shrinkedHeight.current = contentHeight;
      }

      // we don't need to shrink on first render
      if (shrinkedHeight.current && fullHeight.current) {
        shrink(element, shrinkedHeight.current, fullHeight.current);
      }
    } else {
      // contentHeight when grow will change from shrinked height -> full height. We pick full height.
      fullHeight.current = Math.max(fullHeight.current ?? 0, contentHeight);

      if (shrinkedHeight.current !== fullHeight.current) {
        grow(element, shrinkedHeight.current, fullHeight.current);
      }
    }
  }, [isGrown, contentHeight]);

  return (
    // @ts-expect-error styled-component typed this incorrectly
    <GrowContent ref={growRef} className={className} {...others}>
      {children}
    </GrowContent>
  );
};

export { Grow };
